import { useEffect, useState } from "react";
import Avatar, { AvatarProps } from "@mui/material/Avatar";
import { EmtIcon } from "./EmtIcon";
import { styled } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import { EmtTheme } from "../types";

const notificationsFontSize = {
  small: 9,
  medium: 9,
  large: 10,
};

const StyledOnlineBadge = styled(Badge)(({ theme }: { theme?: EmtTheme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme?.palette.success[550],
    boxShadow: `0 0 0 2px ${theme?.palette.background?.paper}`,
  },
}));

const StyledNotificationsBadge = styled(Badge)(
  ({ theme, ...rest }: { theme?: EmtTheme; res: any }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: theme?.palette.error[500],
      color: theme?.palette.common.white,
      padding: 0,
    },
  })
);

const StyledAddBadge = styled(Badge)(({ theme }: { theme?: EmtTheme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme?.palette.info[500],
    color: theme?.palette.common.white,
    padding: 0,
    "& .emt-icons": {
      fontSize: 8,
      fontWeight: "bold",
      top: 1,
      position: "relative",
    },
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }: { theme?: EmtTheme }) => ({
  background: theme?.palette.greyGradient[100],
}));

export type EmtAvatarProps = Omit<AvatarProps, "variant"> & {
  size?: "small" | "medium" | "large";
  onlineBadge?: boolean;
  onlineBadgePosition?: "top" | "bottom";
  notifications?: number;
  notificationsPosition?: "top" | "bottom";
  addBadge?: boolean;
  addBadgePosition?: "top" | "bottom";
  nonce?: string;
  variant?: any | "roundeddashed" | "dashed";
  icon?: string;
};

export function EmtAvatar(props: EmtAvatarProps) {
  const {
    sx,
    size = "medium",
    addBadge = false,
    addBadgePosition = "bottom",
    notifications = 0,
    notificationsPosition = "bottom",
    onlineBadge = false,
    onlineBadgePosition = "bottom",
    ...rest
  } = props;

  // Extract the src from the rest props.
  const { src } = rest;
  const [imageLoaded, setImageLoaded] = useState(false);

  // Preload the image
  useEffect(() => {
    if (src) {
      const img = new Image();
      img.src = src;
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageLoaded(false);
    } else {
      setImageLoaded(false);
    }
  }, [src]);

  const avatarProps = {
    ...rest,
    src: imageLoaded ? src : undefined,
    sizes: size ? size : props.sizes ? props.sizes : "medium",
    sx: {
      ...(rest.onClick ? { cursor: "pointer" } : {}),
      ...sx,
    },
    children:
      props.variant === "dashed" || props.variant === "roundeddashed" ? (
        <EmtIcon className="emt-add" />
      ) : !rest.children ? (
        <EmtIcon
          className={props.icon || "emt-dog"}
          sx={{
            color: (theme: any) => theme.palette.greyGradient[150],
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      ) : (
        rest.children
      ),
  };

  return onlineBadge && notifications <= 0 ? (
    <StyledOnlineBadge
      overlap="circular"
      anchorOrigin={{ vertical: onlineBadgePosition, horizontal: "right" }}
      variant="dot"
    >
      <StyledAvatar {...avatarProps} />
    </StyledOnlineBadge>
  ) : notifications > 0 ? (
    <StyledNotificationsBadge
      overlap="circular"
      anchorOrigin={{ vertical: notificationsPosition, horizontal: "right" }}
      badgeContent={notifications}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: notificationsFontSize[size] as number,
        },
      }}
      res={undefined}
    >
      <StyledAvatar {...avatarProps} />
    </StyledNotificationsBadge>
  ) : addBadge ? (
    <StyledAddBadge
      overlap="circular"
      anchorOrigin={{ vertical: addBadgePosition, horizontal: "right" }}
      badgeContent={<EmtIcon className="emt-add" />}
      sx={{
        "& .MuiBadge-badge": {
          fontSize: notificationsFontSize[size],
        },
      }}
    >
      <StyledAvatar {...avatarProps} />
    </StyledAddBadge>
  ) : (
    <StyledAvatar {...avatarProps} />
  );
}
